import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { focusElement } from '../../utils/useFocusOnOpen';
import { trapTab } from '../../utils/focusTrap';
import { reportTab } from '../../utils/issueReports';
import { canUseWorkspace } from '../../utils/workspace';
import IntakeForm from '../intake/IntakeForm';
import FieldCollectionPicker from './FieldCollectionPicker';
import FieldIntakeForm from './FieldIntakeForm';
import { canDo } from './stages';

/*
 * "+ Add isolate / dish" ("הוספת תבדיד / צלחת"): every way something new
 * comes into the bank, from Home's header, in one dialog.
 *
 * First, where it comes from -- three cards:
 *
 *   preservation  Preservation / Storage: tubes already preserved (bank
 *                 stock). The intake form (IntakeForm) set to bank stock
 *                 alone: the tubes go into their place in the same request
 *                 that registers them (/slants/external-intake).
 *   external      External reception: a raw specimen or tissue, a live
 *                 culture, or a report only -- the intake form with those.
 *   field         Field collection: FieldIntakeForm, saved as a Field Mode
 *                 batch and sent by its outbox.
 *
 * A card is offered to whoever the server lets do what it leads to: bank
 * stock to a lab writer (IntakeForm's LAB_MATERIAL_ROLES), an intake to the
 * intake roles (canDo 'newIntake'), a field collection to anyone Field Mode
 * is for (canUseWorkspace 'field'). AddIsolateModal.canAdd says whether
 * there is any card at all, for the header's button.
 *
 * A labelled modal dialog over a backdrop, as the hub's drawers are: its
 * heading focused as it opens, Escape or × to close, Tab kept inside it, and
 * focus handed back to the button that opened it. While a form is sending,
 * Escape, × and "Other source" do nothing (× is disabled and says why): the
 * request is on its way, and closing would only lose its answer. An intake
 * whose record was saved but whose tubes were refused closes through the
 * form (its `ref`), so that is said rather than lost.
 *
 * Props:
 *   open, user, onClose
 *   onIntakeDone(result)  IntakeForm's onDone, as IntakeDrawer's
 *   onFieldSaved(batch, { queued })  FieldIntakeForm's onSaved
 *   onSettled             IntakeForm's onSettled
 *   store, outbox         Field Mode's device store and outbox
 */
const LAB_MATERIAL_ROLES = ['collection_manager', 'lab_technician', 'lab_manager'];
export const SOURCES = [
  { key: 'preservation', icon: '🧊', sourceTypes: ['bank_stock'], initialSource: 'bank_stock' },
  { key: 'external', icon: '📦', sourceTypes: ['tissue', 'culture', 'info_only'] },
  { key: 'field', icon: '📷' },
];

/** The cards this person may use. */
export function sourcesFor(user) {
  const labWriter = (user?.roles || []).some((r) => LAB_MATERIAL_ROLES.includes(r));
  return SOURCES.filter((s) => (s.key === 'preservation' ? labWriter && canDo(user, 'newIntake')
    : s.key === 'external' ? canDo(user, 'newIntake')
      : canUseWorkspace(user, 'field')));
}

/** Whether Home shows "+ Add isolate / dish" at all. */
export const canAdd = (user) => sourcesFor(user).length > 0;

/** Who may start plates for a collection (routes/plates.py's writers). */
export const PLATE_ROLES = ['collection_manager', 'lab_technician', 'lab_manager'];
export const canStartPlates = (user) => (user?.roles || []).some((r) => PLATE_ROLES.includes(r));

export default function AddIsolateModal({
  open, user, onClose, onIntakeDone, onFieldSaved, onSettled, onPlatesStarted, onOpenIsolate, store, outbox,
}) {
  const { t } = useTranslation(['isolates', 'external', 'common']);
  const [source, setSource] = useState(null);
  // The field card asks first: an existing collection from the Field Mode
  // gallery (its plates), or a new collection from scratch.
  const [fieldMode, setFieldMode] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const formRef = useRef(null);
  const headingRef = useRef(null);
  const dialogRef = useRef(null);
  const opener = useRef(null);
  const options = sourcesFor(user);

  const onBusyChange = (value) => {
    busyRef.current = value;
    setBusy(value);
  };

  // Each opening starts at the cards -- or straight at the one card there is.
  useEffect(() => {
    if (!open) return;
    setSource(options.length === 1 ? options[0].key : null);
    setFieldMode(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // The media list, read each time it opens (IntakeDrawer's reason).
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    api.get('/protocols')
      .then((res) => { if (live) setProtocols(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => {});
    return () => { live = false; };
  }, [open]);

  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });
  // × and Escape: nothing mid-request; an intake form closes itself (its
  // saved-without-tubes warning); anything else just closes.
  const requestClose = () => {
    if (busyRef.current) return;
    if (formRef.current) formRef.current.close();
    else closeRef.current?.();
  };
  const requestCloseRef = useRef(requestClose);
  useEffect(() => { requestCloseRef.current = requestClose; });

  useEffect(() => {
    if (!open) return undefined;
    opener.current = typeof document !== 'undefined' ? document.activeElement : null;
    focusElement(headingRef.current);
    const onKey = (e) => {
      // A dialog opened inside this one (the handoff request, a photo
      // viewer) keeps its own keys: its Tab is already moved, its Escape
      // closes it, not this one too.
      const owner = e.target?.closest?.('[role="dialog"]');
      if (owner && owner !== dialogRef.current) return;
      if (e.key === 'Escape') {
        requestCloseRef.current();
        return;
      }
      // Already moved by an inner dialog's own trap (focus on the Report an
      // issue tab while one is open): moving it again would skip a stop.
      if (e.key !== 'Tab' || !dialogRef.current || e.defaultPrevented) return;
      // Tab and Shift+Tab stay inside the dialog, Report an issue's tab with it.
      trapTab(dialogRef.current, e, { extra: reportTab() });
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Back to whoever opened it.
      const back = opener.current;
      if (back && typeof back.focus === 'function' && back.isConnected !== false) back.focus({ preventScroll: true });
    };
  }, [open]);

  // A new card's form takes focus at its heading, as the dialog's did.
  useEffect(() => { if (open && source) focusElement(headingRef.current); }, [open, source]);

  if (!open) return null;
  const chosen = SOURCES.find((s) => s.key === source) || null;
  const plateStarter = canStartPlates(user);
  const fieldChoices = chosen?.key === 'field' && plateStarter;
  const title = chosen?.key === 'field' && fieldMode === 'existing' ? t('add.field.existingTitle')
    : chosen ? t(`add.source.${chosen.key}.title`) : t('add.title');
  const back = () => {
    if (busyRef.current) return;
    formRef.current = null;
    if (fieldChoices && fieldMode) setFieldMode(null);
    else setSource(null);
  };

  return (
    <div className="hub-drawer-backdrop" data-testid="add-isolate-backdrop">
      <aside ref={dialogRef} className="hub-drawer hub-drawer--wide" role="dialog" aria-modal="true"
        aria-labelledby="add-isolate-title" onClick={(e) => e.stopPropagation()} data-testid="add-isolate-modal">
        <div className="hub-drawer__head" style={{ marginBottom: 12 }}>
          <h3 id="add-isolate-title" ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 17 }}>
            {title}
          </h3>
          <button type="button" onClick={requestClose} disabled={busy}
            title={busy ? t('external:source.tip.closeBusy') : t('tip.closeDrawer')} aria-label={t('tip.closeDrawer')}
            data-testid="add-isolate-close"
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: busy ? 'not-allowed' : 'pointer',
              color: 'var(--c-text-muted-fg)', lineHeight: 1, opacity: busy ? 0.5 : 1, minWidth: 44, minHeight: 44 }}>
            ×
          </button>
        </div>

        {chosen && (options.length > 1 || (fieldChoices && fieldMode)) && (
          <button type="button" onClick={back}
            disabled={busy} data-testid="add-isolate-back" title={t('add.tip.back')}
            style={{ background: 'none', border: 'none', padding: '0 0 10px', color: 'var(--c-info-fg)', cursor: 'pointer',
              font: 'inherit', fontSize: 14, textDecoration: 'underline', minHeight: 44 }}>
            {t('add.back')}
          </button>
        )}

        {!chosen && (
          <>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--c-text-muted-fg)' }}>{t('add.intro')}</p>
            <div role="list" style={{ display: 'grid', gap: 10,
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))' }}>
              {options.map((s) => (
                <div role="listitem" key={s.key}>
                  <button type="button" onClick={() => setSource(s.key)} data-testid={`add-isolate-source-${s.key}`}
                    aria-describedby={`add-isolate-${s.key}-desc`} title={t(`add.source.${s.key}.tip`)}
                    style={card}>
                    <span aria-hidden="true" style={{ fontSize: 26, lineHeight: 1 }}>{s.icon}</span>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{t(`add.source.${s.key}.title`)}</span>
                    <span id={`add-isolate-${s.key}-desc`} style={{ fontSize: 13, color: 'var(--c-text-muted-fg)',
                      lineHeight: 1.4 }}>
                      {t(`add.source.${s.key}.desc`)}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {chosen && chosen.key !== 'field' && (
          <IntakeForm key={chosen.key} ref={formRef} user={user} protocols={protocols} compact
            sourceTypes={chosen.sourceTypes} initialSource={chosen.initialSource || ''}
            onDone={onIntakeDone} onCancel={onClose} onBusyChange={onBusyChange} onSettled={onSettled} />
        )}
        {chosen && chosen.key === 'field' && fieldChoices && !fieldMode && (
          <>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--c-text-muted-fg)' }}>{t('add.field.intro')}</p>
            <div role="list" style={{ display: 'grid', gap: 10,
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))' }}>
              {['existing', 'new'].map((key) => (
                <div role="listitem" key={key}>
                  <button type="button" onClick={() => setFieldMode(key)} data-testid={`add-isolate-field-${key}`}
                    aria-describedby={`add-isolate-field-${key}-desc`} title={t(`add.field.${key}Tip`)} style={card}>
                    <span aria-hidden="true" style={{ fontSize: 26, lineHeight: 1 }}>{key === 'existing' ? '🖼️' : '✏️'}</span>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{t(`add.field.${key}Title`)}</span>
                    <span id={`add-isolate-field-${key}-desc`} style={{ fontSize: 13, color: 'var(--c-text-muted-fg)', lineHeight: 1.4 }}>
                      {t(`add.field.${key}Desc`)}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
        {chosen && chosen.key === 'field' && fieldChoices && fieldMode === 'existing' && (
          <FieldCollectionPicker user={user} protocols={protocols} t={t} onBusyChange={onBusyChange}
            onDone={(message, collection) => onPlatesStarted?.(message, collection)}
            onOpenIsolate={(code, path) => onOpenIsolate?.(code, path)} onCancel={() => setFieldMode(null)} />
        )}
        {chosen && chosen.key === 'field' && (!fieldChoices || fieldMode === 'new') && (
          <FieldIntakeForm user={user} store={store} outbox={outbox} onSaved={onFieldSaved} onCancel={onClose}
            onBusyChange={onBusyChange} />
        )}
      </aside>
    </div>
  );
}

const card = {
  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, textAlign: 'start', width: '100%',
  minHeight: 120, padding: '14px 16px', borderRadius: 12, cursor: 'pointer', font: 'inherit',
  color: 'var(--c-text-fg)', background: 'var(--c-surface)', border: '2px solid var(--c-border)',
  boxSizing: 'border-box',
};
